import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  Users, Phone, Mail, Building2, Plus, Search, Filter, MoreVertical,
  Flame, TrendingUp, Clock, MapPin, ExternalLink, Upload, Download,
  Send, PhoneCall, MessageSquare, Edit, Trash2, Eye, RefreshCw,
  Loader2, LogOut, User, ChevronRight, FileText, Target, Zap, Home
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const statusColors = {
  cold: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  warm: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  hot: 'bg-red-500/20 text-red-400 border-red-500/30',
  converted: 'bg-green-500/20 text-green-400 border-green-500/30',
  lost: 'bg-slate-500/20 text-slate-400 border-slate-500/30'
};

export default function NESCRMPage() {
  const navigate = useNavigate();
  
  // Auth state
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  
  // Data state
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, cold: 0, warm: 0, hot: 0, converted: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [interactions, setInteractions] = useState([]);
  
  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showAddLead, setShowAddLead] = useState(false);
  const [showLeadDetail, setShowLeadDetail] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  
  // Form state
  const [newLead, setNewLead] = useState({
    name: '', address: '', phone: '', email: '', website: '', business_type: '', notes: ''
  });
  const [emailForm, setEmailForm] = useState({ subject: '', body: '' });
  const [importData, setImportData] = useState('');

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, { credentials: 'include' });
        if (!response.ok) throw new Error('Not authenticated');
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
      } catch (e) {
        setIsAuthenticated(false);
        navigate('/login', { replace: true });
      }
    };
    checkAuth();
  }, [navigate]);

  // Load data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadDashboard();
      loadLeads();
    }
  }, [isAuthenticated]);

  const loadDashboard = async () => {
    try {
      const res = await fetch(`${API}/nes/dashboard`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (e) {
      console.error('Dashboard load error:', e);
    }
  };

  const loadLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const res = await fetch(`${API}/nes/leads?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setLeads(data.leads || []);
      }
    } catch (e) {
      console.error('Leads load error:', e);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const loadInteractions = async (leadId) => {
    try {
      const res = await fetch(`${API}/nes/leads/${leadId}/interactions`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setInteractions(data.interactions || []);
      }
    } catch (e) {
      console.error('Interactions load error:', e);
    }
  };

  const handleCreateLead = async () => {
    try {
      const res = await fetch(`${API}/nes/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newLead)
      });
      
      if (res.ok) {
        toast.success('Lead created successfully');
        setShowAddLead(false);
        setNewLead({ name: '', address: '', phone: '', email: '', website: '', business_type: '', notes: '' });
        loadLeads();
        loadDashboard();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create lead');
      }
    } catch (e) {
      toast.error('Failed to create lead');
    }
  };

  const handleUpdateStatus = async (leadId, newStatus) => {
    try {
      const res = await fetch(`${API}/nes/leads/${leadId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });
      
      if (res.ok) {
        toast.success('Status updated');
        loadLeads();
        loadDashboard();
        if (selectedLead?.id === leadId) {
          setSelectedLead({ ...selectedLead, status: newStatus });
        }
      }
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!confirm('Are you sure you want to delete this lead?')) return;
    
    try {
      const res = await fetch(`${API}/nes/leads/${leadId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.ok) {
        toast.success('Lead deleted');
        setShowLeadDetail(false);
        loadLeads();
        loadDashboard();
      }
    } catch (e) {
      toast.error('Failed to delete lead');
    }
  };

  const handleSendEmail = async () => {
    if (!selectedLead?.email) {
      toast.error('Lead has no email address');
      return;
    }
    
    try {
      const res = await fetch(`${API}/nes/leads/${selectedLead.id}/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(emailForm)
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success('Email sent successfully');
        setShowEmailDialog(false);
        setEmailForm({ subject: '', body: '' });
        loadInteractions(selectedLead.id);
      } else {
        toast.error(data.error || 'Failed to send email');
      }
    } catch (e) {
      toast.error('Failed to send email');
    }
  };

  const handleImportLeads = async () => {
    try {
      const leadsData = JSON.parse(importData);
      const dataArray = Array.isArray(leadsData) ? leadsData : [leadsData];
      
      const res = await fetch(`${API}/nes/leads/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ leads: dataArray, source: 'import' })
      });
      
      const result = await res.json();
      toast.success(`Imported ${result.imported} leads`);
      if (result.errors > 0) {
        toast.warning(`${result.errors} leads failed to import`);
      }
      
      setShowImportDialog(false);
      setImportData('');
      loadLeads();
      loadDashboard();
    } catch (e) {
      toast.error('Invalid JSON format');
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch (e) {
      console.warn('Logout error (ignored):', e.message);
    }
    navigate('/login', { replace: true });
  };

  const viewLeadDetails = async (lead) => {
    setSelectedLead(lead);
    setShowLeadDetail(true);
    await loadInteractions(lead.id);
  };

  const filteredLeads = useMemo(() => {
    return leads.filter(lead => {
      const matchesSearch = !searchQuery || 
        lead.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        lead.business_type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        lead.email?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    });
  }, [leads, searchQuery]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-950/90 backdrop-blur-lg border-b border-slate-800">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                  <Flame className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-white">NES CRM</h1>
                  <p className="text-xs text-slate-400">Commercial Clients</p>
                </div>
              </Link>
            </div>
            
            <div className="flex items-center gap-3">
              <Link to="/">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white" data-testid="home-link">
                  <Home className="w-4 h-4 mr-2" />
                  Website
                </Button>
              </Link>
              
              {user && (
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                  ) : (
                    <User className="w-5 h-5" />
                  )}
                  <span className="hidden sm:inline">{user.name}</span>
                </div>
              )}
              
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-slate-400 hover:text-white"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-8">
        {/* Dashboard Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8"
        >
          {[
            { label: 'Total Leads', value: stats.total, icon: Users, color: 'slate' },
            { label: 'Cold', value: stats.cold, icon: Target, color: 'blue' },
            { label: 'Warm', value: stats.warm, icon: TrendingUp, color: 'orange' },
            { label: 'Hot', value: stats.hot, icon: Zap, color: 'red' },
            { label: 'Converted', value: stats.converted, icon: Building2, color: 'green' }
          ].map((stat) => (
            <Card key={stat.label} className={`bg-${stat.color}-500/10 border-${stat.color}-500/30`} data-testid={`stat-${stat.label.toLowerCase()}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <stat.icon className={`w-5 h-5 text-${stat.color}-400`} />
                  <span className={`text-2xl font-bold text-${stat.color}-400`}>{stat.value}</span>
                </div>
                <p className="text-xs text-slate-400">{stat.label}</p>
              </CardContent>
            </Card>
          ))}
        </motion.div>

        {/* Action Bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search leads..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-slate-800 border-slate-700 text-white"
              data-testid="search-input"
            />
          </div>
          
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setTimeout(loadLeads, 0); }}>
            <SelectTrigger className="w-[150px] bg-slate-800 border-slate-700 text-white" data-testid="status-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="cold">Cold</SelectItem>
              <SelectItem value="warm">Warm</SelectItem>
              <SelectItem value="hot">Hot</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
            </SelectContent>
          </Select>
          
          <div className="flex gap-2">
            <Button
              onClick={() => setShowImportDialog(true)}
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
              data-testid="import-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import
            </Button>
            
            <Button
              onClick={() => setShowAddLead(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="add-lead-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Lead
            </Button>
          </div>
        </div>

        {/* Leads Table */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-orange-400" />
              Business Leads
            </CardTitle>
            <CardDescription className="text-slate-400">
              Manage your commercial client leads
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
              </div>
            ) : filteredLeads.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No leads found</p>
                <Button
                  onClick={() => setShowAddLead(true)}
                  className="mt-4 bg-orange-500 hover:bg-orange-600"
                >
                  Add Your First Lead
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="leads-table">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Business</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium hidden md:table-cell">Type</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium hidden lg:table-cell">Contact</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Score</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLeads.map((lead) => (
                      <tr key={lead.id} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors">
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium text-white">{lead.name}</p>
                            <p className="text-xs text-slate-400 truncate max-w-[200px]">{lead.address}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4 hidden md:table-cell">
                          <span className="text-slate-300 capitalize">{lead.business_type || '-'}</span>
                        </td>
                        <td className="py-3 px-4 hidden lg:table-cell">
                          <div className="text-sm">
                            {lead.phone && (
                              <p className="text-slate-300 flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {lead.phone}
                              </p>
                            )}
                            {lead.email && (
                              <p className="text-slate-400 flex items-center gap-1 truncate max-w-[150px]">
                                <Mail className="w-3 h-3" /> {lead.email}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Progress value={lead.lpg_score} className="w-16 h-2" />
                            <span className="text-sm text-slate-300">{lead.lpg_score}%</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Select
                            value={lead.status}
                            onValueChange={(v) => handleUpdateStatus(lead.id, v)}
                          >
                            <SelectTrigger className={`w-[100px] h-8 text-xs border ${statusColors[lead.status]} bg-transparent`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-800 border-slate-700">
                              <SelectItem value="cold">Cold</SelectItem>
                              <SelectItem value="warm">Warm</SelectItem>
                              <SelectItem value="hot">Hot</SelectItem>
                              <SelectItem value="converted">Converted</SelectItem>
                              <SelectItem value="lost">Lost</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => viewLeadDetails(lead)}
                              className="text-slate-400 hover:text-white"
                              data-testid={`view-lead-${lead.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {lead.email && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => { setSelectedLead(lead); setShowEmailDialog(true); }}
                                className="text-blue-400 hover:text-blue-300"
                                data-testid={`email-lead-${lead.id}`}
                              >
                                <Mail className="w-4 h-4" />
                              </Button>
                            )}
                            {lead.phone && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => window.open(`tel:${lead.phone}`)}
                                className="text-green-400 hover:text-green-300"
                              >
                                <Phone className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Add Lead Dialog */}
      <Dialog open={showAddLead} onOpenChange={setShowAddLead}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New Lead</DialogTitle>
            <DialogDescription className="text-slate-400">
              Add a new business lead to your CRM
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Business Name *</Label>
                <Input
                  value={newLead.name}
                  onChange={(e) => setNewLead({ ...newLead, name: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  data-testid="new-lead-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Business Type</Label>
                <Select
                  value={newLead.business_type}
                  onValueChange={(v) => setNewLead({ ...newLead, business_type: v })}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="new-lead-type">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="restaurant">Restaurant</SelectItem>
                    <SelectItem value="hotel">Hotel</SelectItem>
                    <SelectItem value="factory">Factory</SelectItem>
                    <SelectItem value="hospital">Hospital</SelectItem>
                    <SelectItem value="school">School</SelectItem>
                    <SelectItem value="catering">Catering</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={newLead.address}
                onChange={(e) => setNewLead({ ...newLead, address: e.target.value })}
                className="bg-slate-800 border-slate-700"
                data-testid="new-lead-address"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={newLead.phone}
                  onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  data-testid="new-lead-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newLead.email}
                  onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  data-testid="new-lead-email"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Website</Label>
              <Input
                value={newLead.website}
                onChange={(e) => setNewLead({ ...newLead, website: e.target.value })}
                className="bg-slate-800 border-slate-700"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={newLead.notes}
                onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
                className="bg-slate-800 border-slate-700"
                data-testid="new-lead-notes"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddLead(false)} className="border-slate-700">
              Cancel
            </Button>
            <Button
              onClick={handleCreateLead}
              disabled={!newLead.name}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="save-lead-btn"
            >
              Save Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lead Detail Dialog */}
      <Dialog open={showLeadDetail} onOpenChange={setShowLeadDetail}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedLead && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <DialogTitle className="text-xl">{selectedLead.name}</DialogTitle>
                    <DialogDescription className="text-slate-400">
                      {selectedLead.business_type && (
                        <Badge variant="outline" className="mr-2 capitalize">{selectedLead.business_type}</Badge>
                      )}
                      <Badge className={statusColors[selectedLead.status]}>{selectedLead.status}</Badge>
                    </DialogDescription>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-400">LPG Score</p>
                    <p className="text-2xl font-bold text-orange-400">{selectedLead.lpg_score}%</p>
                  </div>
                </div>
              </DialogHeader>
              
              <div className="grid md:grid-cols-2 gap-6 mt-4">
                <div className="space-y-4">
                  <h4 className="font-semibold text-slate-200 flex items-center gap-2">
                    <Building2 className="w-4 h-4" /> Contact Info
                  </h4>
                  
                  {selectedLead.address && (
                    <p className="text-sm text-slate-300 flex items-start gap-2">
                      <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      {selectedLead.address}
                    </p>
                  )}
                  
                  {selectedLead.phone && (
                    <p className="text-sm text-slate-300 flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      <a href={`tel:${selectedLead.phone}`} className="hover:text-orange-400">{selectedLead.phone}</a>
                    </p>
                  )}
                  
                  {selectedLead.email && (
                    <p className="text-sm text-slate-300 flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      <a href={`mailto:${selectedLead.email}`} className="hover:text-orange-400">{selectedLead.email}</a>
                    </p>
                  )}
                  
                  {selectedLead.website && (
                    <p className="text-sm text-slate-300 flex items-center gap-2">
                      <ExternalLink className="w-4 h-4" />
                      <a href={selectedLead.website} target="_blank" rel="noopener noreferrer" className="hover:text-orange-400">
                        {selectedLead.website}
                      </a>
                    </p>
                  )}
                  
                  {selectedLead.notes && (
                    <div className="mt-4 p-3 bg-slate-800 rounded-lg">
                      <p className="text-xs text-slate-400 mb-1">Notes</p>
                      <p className="text-sm text-slate-300">{selectedLead.notes}</p>
                    </div>
                  )}
                </div>
                
                <div className="space-y-4">
                  <h4 className="font-semibold text-slate-200 flex items-center gap-2">
                    <Clock className="w-4 h-4" /> Interaction History
                  </h4>
                  
                  {interactions.length === 0 ? (
                    <p className="text-sm text-slate-500">No interactions yet</p>
                  ) : (
                    <div className="space-y-3 max-h-[200px] overflow-y-auto">
                      {interactions.map((int) => (
                        <div key={int.id || int.created_at} className="p-3 bg-slate-800 rounded-lg">
                          <div className="flex items-center justify-between mb-1">
                            <Badge variant="outline" className="text-xs capitalize">{int.interaction_type}</Badge>
                            <span className="text-xs text-slate-500">
                              {new Date(int.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-sm text-slate-300 line-clamp-2">{int.content}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <DialogFooter className="mt-6 flex-wrap gap-2">
                <Button
                  variant="destructive"
                  onClick={() => handleDeleteLead(selectedLead.id)}
                  className="mr-auto"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
                
                {selectedLead.email && (
                  <Button
                    onClick={() => setShowEmailDialog(true)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Send Email
                  </Button>
                )}
                
                {selectedLead.phone && (
                  <Button
                    onClick={() => window.open(`tel:${selectedLead.phone}`)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Call Now
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Send Email</DialogTitle>
            <DialogDescription className="text-slate-400">
              Send email to {selectedLead?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Subject *</Label>
              <Input
                value={emailForm.subject}
                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                className="bg-slate-800 border-slate-700"
                placeholder="Commercial LPG Solutions for Your Business"
                data-testid="email-subject"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Message *</Label>
              <Textarea
                value={emailForm.body}
                onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
                className="bg-slate-800 border-slate-700 min-h-[200px]"
                placeholder="Dear [Business Name],&#10;&#10;We specialize in providing reliable commercial LPG solutions..."
                data-testid="email-body"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)} className="border-slate-700">
              Cancel
            </Button>
            <Button
              onClick={handleSendEmail}
              disabled={!emailForm.subject || !emailForm.body}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="send-email-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import Leads</DialogTitle>
            <DialogDescription className="text-slate-400">
              Paste JSON data to import leads from LeadsFynder or other sources
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>JSON Data</Label>
              <Textarea
                value={importData}
                onChange={(e) => setImportData(e.target.value)}
                className="bg-slate-800 border-slate-700 min-h-[300px] font-mono text-sm"
                placeholder={`[
  {
    "name": "Business Name",
    "address": "123 Main St",
    "phone": "+27123456789",
    "email": "contact@business.com",
    "business_type": "restaurant"
  }
]`}
                data-testid="import-data"
              />
            </div>
            
            <p className="text-xs text-slate-500">
              Tip: Export leads from LeadsFynder and paste the JSON here. Required field: name
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)} className="border-slate-700">
              Cancel
            </Button>
            <Button
              onClick={handleImportLeads}
              disabled={!importData}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="import-leads-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import Leads
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
